import { Save } from 'lucide-react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { useTeamTab } from '../../TeamTabContext';
import {
  TEAM_ROSTER_GRID_MIN_WIDTH,
  TEAM_ROSTER_GRID_TEMPLATE_COLUMNS,
  TEAM_ROSTER_VIRTUAL_LIST_MAX_HEIGHT,
  TEAM_ROSTER_VIRTUAL_ROW_SIZE,
  TEAM_ROSTER_VIRTUALIZE_THRESHOLD,
} from '../../teamConstants';
import {
  filterRosterPersons,
  rosterCapacitySnapshot,
  teamMemberRowForPerson,
} from '../../teamRosterRowHelpers';
import { RosterInviteModal } from '../../modals/RosterInviteModal';
import { RemoveOrgMemberConfirm } from '../../modals/RemoveOrgMemberConfirm';
import { DistributionCriteriaPanel } from '../distribution/DistributionCriteriaPanel';
import { TeamRosterFilterBar } from './TeamRosterFilterBar';
import { TeamRosterGridRow } from './TeamRosterGridRow';
import { TeamRosterTableHeader } from './TeamRosterTableHeader';
import { TeamRosterListInner, TeamRosterVirtualRow } from './TeamRosterVirtualRow';

const ROSTER_PAGE_SIZE = 50;

export const TeamRosterPanel: React.FC = () => {
  const ctx = useTeamTab();
  const {
    teamRosterSectionRef,
    highlightTeamRosterSection,
    teamMembers,
    setTeamMembers,
    rosterPersonsList,
    rosterFetchError,
    loadingTeamMembers,
    rosterSearch,
    setRosterSearch,
    rosterSortBy,
    setRosterSortBy,
    rosterSortDir,
    setRosterSortDir,
    rosterPage,
    setRosterPage,
    canModifyTeam,
    handleUpdateTeamMember,
    reloadTeamMembers,
    savingCapacity,
    setSavingCapacity,
    teamRosterSaved,
    setTeamRosterSaved,
    teamRosterSaveError,
    setTeamRosterSaveError,
    originalTeamMembers,
    availableRoles,
  } = ctx;

  const rosterViewportRef = useRef<HTMLDivElement>(null);
  const rosterHeaderClipRef = useRef<HTMLDivElement>(null);
  const [rosterBodyEl, setRosterBodyEl] = useState<HTMLDivElement | null>(null);
  const [rosterListWidth, setRosterListWidth] = useState(1);
  const [hideDeactivated, setHideDeactivated] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  useLayoutEffect(() => {
    const el = rosterViewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const apply = () => setRosterListWidth(Math.max(1, Math.floor(el.clientWidth)));
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    apply();
    return () => ro.disconnect();
  }, []);

  const syncRosterHeaderScroll = (body: HTMLElement) => {
    const header = rosterHeaderClipRef.current;
    if (header) header.scrollLeft = body.scrollLeft;
  };

  useLayoutEffect(() => {
    if (!rosterBodyEl) return;
    const onScroll = () => syncRosterHeaderScroll(rosterBodyEl);
    rosterBodyEl.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => rosterBodyEl.removeEventListener('scroll', onScroll);
  }, [rosterBodyEl]);

  const rosterPageDerived = useMemo(() => {
    const filteredPersons = filterRosterPersons(rosterPersonsList, teamMembers, {
      hideDeactivated,
      selectedRoles,
      search: rosterSearch,
    });
    const sortedPersons = [...filteredPersons].sort((a, b) => {
      const dir = rosterSortDir === 'asc' ? 1 : -1;
      const aMember = teamMemberRowForPerson(teamMembers, a);
      const bMember = teamMemberRowForPerson(teamMembers, b);
      const aName = typeof a.display_name === 'string' ? a.display_name : aMember?.name || '';
      const bName = typeof b.display_name === 'string' ? b.display_name : bMember?.name || '';
      if (rosterSortBy === 'name') return dir * aName.localeCompare(bName);
      if (rosterSortBy === 'role')
        return dir * (aMember?.role || '').localeCompare(bMember?.role || '');
      if (rosterSortBy === 'capacity')
        return dir * ((aMember?.capacityHoursPerDay ?? 8) - (bMember?.capacityHoursPerDay ?? 8));
      return 0;
    });
    const filteredCount = sortedPersons.length;
    const totalPages = Math.max(1, Math.ceil(filteredCount / ROSTER_PAGE_SIZE));
    const safePage = Math.min(rosterPage, totalPages - 1);
    const pageItems = sortedPersons.slice(
      safePage * ROSTER_PAGE_SIZE,
      (safePage + 1) * ROSTER_PAGE_SIZE
    );
    return { pageItems, filteredCount, totalPages, safePage };
  }, [
    rosterSearch,
    hideDeactivated,
    selectedRoles,
    rosterPersonsList,
    teamMembers,
    rosterSortBy,
    rosterSortDir,
    rosterPage,
  ]);

  const rosterVirtualItemData = useMemo(
    () => ({ ctx, pageItems: rosterPageDerived.pageItems }),
    [ctx, rosterPageDerived.pageItems]
  );

  const { pageItems, filteredCount, totalPages, safePage } = rosterPageDerived;

  const rosterListBodyHeight = Math.min(
    TEAM_ROSTER_VIRTUAL_LIST_MAX_HEIGHT,
    Math.max(TEAM_ROSTER_VIRTUAL_ROW_SIZE, pageItems.length * TEAM_ROSTER_VIRTUAL_ROW_SIZE)
  );

  const capacitySnapshotChanged = teamMembers.some((member) => {
    if (!member.id) return false;
    const original = originalTeamMembers.find((om) => om.id === member.id);
    if (!original) return false;
    return rosterCapacitySnapshot(member) !== rosterCapacitySnapshot(original);
  });

  const start = safePage * ROSTER_PAGE_SIZE + 1;
  const end = Math.min((safePage + 1) * ROSTER_PAGE_SIZE, filteredCount);

  return (
    <>
      <div
        ref={teamRosterSectionRef as React.Ref<HTMLDivElement>}
        id="profile-team-roster"
        className={highlightTeamRosterSection ? 'profile-team-roster-highlight-section' : undefined}
        style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: 'var(--profile-nested-bg)',
          borderRadius: '12px',
          border: '1px solid var(--profile-nested-border)',
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <h3
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--color-text)',
                margin: 0,
              }}
            >
              Team roster ({rosterPersonsList.length})
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '12px',
                  fontWeight: '400',
                  color: 'var(--color-text-secondary)',
                }}
              >
                (
                {
                  teamMembers.filter(
                    (m) => m.includeInCapacity === true || m.includeInCapacity === undefined
                  ).length
                }{' '}
                included in capacity on current product)
              </span>
            </h3>
          </div>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.4,
            }}
          >
            These rows name people for capacity and synced data. They are not Relix login accounts
            unless the same person was invited to the organization.
          </p>

          <TeamRosterFilterBar
            search={rosterSearch}
            onSearchChange={(value) => {
              setRosterSearch(value);
              setRosterPage(0);
            }}
            hideDeactivated={hideDeactivated}
            onHideDeactivatedChange={(value) => {
              setHideDeactivated(value);
              setRosterPage(0);
            }}
            availableRoles={availableRoles}
            selectedRoles={selectedRoles}
            onSelectedRolesChange={(roles) => {
              setSelectedRoles(roles);
              setRosterPage(0);
            }}
            sortBy={rosterSortBy}
            onSortByChange={setRosterSortBy}
            sortDir={rosterSortDir}
            onSortDirToggle={() => setRosterSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            filteredCount={filteredCount}
            totalCount={rosterPersonsList.length}
          />

          {(capacitySnapshotChanged || teamRosterSaved || teamRosterSaveError) && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '12px',
                marginTop: '16px',
              }}
            >
              {capacitySnapshotChanged && (
                <button
                  type="button"
                  onClick={async () => {
                    setSavingCapacity(true);
                    setTeamRosterSaved(false);
                    setTeamRosterSaveError(null);
                    try {
                      const changed = teamMembers.filter((m) => {
                        if (!m.id) return false;
                        const original = originalTeamMembers.find((om) => om.id === m.id);
                        if (!original) return false;
                        return rosterCapacitySnapshot(m) !== rosterCapacitySnapshot(original);
                      });
                      await Promise.all(changed.map((m) => handleUpdateTeamMember(m)));
                      await reloadTeamMembers();
                      setTeamRosterSaved(true);
                      setTimeout(() => setTeamRosterSaved(false), 5000);
                    } catch (e) {
                      console.error('Error saving capacity settings:', e);
                      setTeamRosterSaveError(
                        e instanceof Error ? e.message : 'Failed to save capacity settings.'
                      );
                    } finally {
                      setSavingCapacity(false);
                    }
                  }}
                  disabled={savingCapacity || !canModifyTeam}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: savingCapacity || !canModifyTeam ? '#9ca3af' : '#6366f1',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: savingCapacity || !canModifyTeam ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Save size={16} />
                  {savingCapacity ? 'Saving...' : 'Save Capacity Settings'}
                </button>
              )}
              {teamRosterSaved && (
                <div className="success-message" role="status">
                  <span className="success-icon">?</span>
                  Capacity settings saved successfully!
                </div>
              )}
              {teamRosterSaveError && (
                <div className="error-message" role="alert" style={{ color: 'var(--color-error)' }}>
                  {teamRosterSaveError}
                </div>
              )}
            </div>
          )}

          {rosterFetchError && (
            <div className="error-message" role="alert" style={{ marginTop: 8 }}>
              <span style={{ color: 'var(--color-error)', display: 'block' }}>
                {rosterFetchError}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 12,
                  marginTop: 6,
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.4,
                }}
              >
                If the API reports a missing table (42P01), apply migration{' '}
                <code style={{ fontSize: 11 }}>033_roster_person_identity.sql</code> to the Postgres
                instance wired to the API (<code>DATABASE_URL</code>
                ).
              </span>
            </div>
          )}

          {loadingTeamMembers ? (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: '14px',
              }}
            >
              Loading roster?
            </div>
          ) : rosterPersonsList.length === 0 && !rosterFetchError ? (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: '14px',
              }}
            >
              No roster entries yet. Add people above (suggestions or manual) so they can appear in
              capacity, Releases, and analytics.
            </div>
          ) : (
            <>
              <div
                ref={rosterViewportRef}
                className="team-roster-table-viewport"
                style={
                  {
                    '--team-roster-grid-template': TEAM_ROSTER_GRID_TEMPLATE_COLUMNS,
                    '--team-roster-grid-min-width': `${TEAM_ROSTER_GRID_MIN_WIDTH}px`,
                  } as React.CSSProperties
                }
              >
                <div ref={rosterHeaderClipRef} className="team-roster-table-header-clip">
                  <TeamRosterTableHeader
                    highlightTeamRosterSection={highlightTeamRosterSection}
                    teamMembers={teamMembers}
                    setTeamMembers={setTeamMembers}
                    canModifyTeam={canModifyTeam}
                  />
                </div>
                {pageItems.length >= TEAM_ROSTER_VIRTUALIZE_THRESHOLD ? (
                  <FixedSizeList
                    className="team-roster-table-body"
                    outerRef={setRosterBodyEl}
                    innerElementType={TeamRosterListInner}
                    height={rosterListBodyHeight}
                    width={rosterListWidth}
                    itemCount={pageItems.length}
                    itemSize={TEAM_ROSTER_VIRTUAL_ROW_SIZE}
                    itemData={rosterVirtualItemData}
                    overscanCount={4}
                    style={{ overflowX: 'scroll', overflowY: 'auto', willChange: 'auto' }}
                  >
                    {TeamRosterVirtualRow}
                  </FixedSizeList>
                ) : (
                  <div
                    className="team-roster-table-body"
                    ref={setRosterBodyEl}
                    style={{ maxHeight: rosterListBodyHeight }}
                  >
                    <div
                      className="team-roster-table-body-inner"
                      style={{ minWidth: TEAM_ROSTER_GRID_MIN_WIDTH }}
                    >
                      {pageItems.map((person) => {
                        const pidStr =
                          typeof person.id === 'string'
                            ? person.id
                            : person.id != null
                              ? String(person.id)
                              : '';
                        return (
                          <div
                            key={pidStr || 'roster-row'}
                            className="team-roster-table-plain-slot"
                          >
                            <TeamRosterGridRow person={person} ctx={ctx} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {filteredCount > ROSTER_PAGE_SIZE && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 12,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    Showing {start}
                    {'\u2013'}
                    {end} of {filteredCount}
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      type="button"
                      disabled={safePage === 0}
                      onClick={() => setRosterPage(0)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-bg-input)',
                        color: 'var(--color-text)',
                        cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                        opacity: safePage === 0 ? 0.5 : 1,
                        fontSize: '13px',
                      }}
                    >
                      First
                    </button>
                    <button
                      type="button"
                      disabled={safePage === 0}
                      onClick={() => setRosterPage((p) => Math.max(0, p - 1))}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-bg-input)',
                        color: 'var(--color-text)',
                        cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                        opacity: safePage === 0 ? 0.5 : 1,
                        fontSize: '13px',
                      }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i)
                      .filter((i) => Math.abs(i - safePage) <= 2 || i === 0 || i === totalPages - 1)
                      .map((i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setRosterPage(i)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid var(--color-border-strong)',
                            borderRadius: 'var(--radius-sm)',
                            background:
                              i === safePage ? 'var(--color-accent)' : 'var(--color-bg-input)',
                            color: i === safePage ? '#fff' : 'var(--color-text)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: i === safePage ? 600 : 400,
                          }}
                        >
                          {i + 1}
                        </button>
                      ))}
                    <button
                      type="button"
                      disabled={safePage >= totalPages - 1}
                      onClick={() => setRosterPage((p) => Math.min(totalPages - 1, p + 1))}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-bg-input)',
                        color: 'var(--color-text)',
                        cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                        opacity: safePage >= totalPages - 1 ? 0.5 : 1,
                        fontSize: '13px',
                      }}
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      disabled={safePage >= totalPages - 1}
                      onClick={() => setRosterPage(totalPages - 1)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-bg-input)',
                        color: 'var(--color-text)',
                        cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                        opacity: safePage >= totalPages - 1 ? 0.5 : 1,
                        fontSize: '13px',
                      }}
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}

              <RosterInviteModal />

              <RemoveOrgMemberConfirm />

              <DistributionCriteriaPanel />
            </>
          )}
        </div>
      </div>
    </>
  );
};
